import { Alerta, Button, Casilla, Combobox, Row, Select, Stack, TextArea, TextField } from "@/components";
import type { AvanceAdmin, EstadoTarea, Novedad } from "@/lib/api";
import { ETIQUETA_ESTADO_TAREA, estadoActual, nombreEtapa, opcionesEtapas } from "@/lib/avance";
import { hoyISO } from "@/lib/format";
import { FotosNovedad } from "./FotosNovedad";
import { useFormularioNovedad } from "./useFormularioNovedad";

export interface FormularioNovedadProps {
  fid: number;
  /** `null` = novedad nueva. */
  novedad: Novedad | null;
  admin: AvanceAdmin;
  onGuardado: () => void;
  onCancelar: () => void;
}

const OPCIONES_ESTADO = (["sin_empezar", "en_curso", "terminada"] as const).map((valor) => ({ valor, etiqueta: ETIQUETA_ESTADO_TAREA[valor] }));

/**
 * Una novedad cuenta qué pasó en la obra y, si hace falta, cambia el estado de una o varias tareas: se agregan de a
 * una con el buscador "Agregar tarea" y para cada una se elige su nuevo estado.
 */
export function FormularioNovedad({ fid, novedad, admin, onGuardado, onCancelar }: FormularioNovedadProps) {
  const f = useFormularioNovedad(fid, novedad, onGuardado);

  return (
    <form onSubmit={(e) => void f.guardar(e)} noValidate>
      <Stack espacio={4}>
        <TextField etiqueta="Fecha" type="date" max={hoyISO()} value={f.form.fecha} onChange={(e) => f.cambiar("fecha", e.target.value)} error={f.errores.fecha} />
        <TextField etiqueta="Título" value={f.form.titulo} maxLength={120} onChange={(e) => f.cambiar("titulo", e.target.value)} error={f.errores.titulo} />
        <TextArea etiqueta="Texto" value={f.form.texto} onChange={(e) => f.cambiar("texto", e.target.value)} error={f.errores.texto} ayuda="Opcional." />

        <Combobox
          etiqueta="Agregar tarea"
          opciones={opcionesEtapas(admin, f.form.cambios.map((c) => c.etapa_id))}
          onElegir={(v) => f.agregarCambio(v)}
          placeholder="Buscá una tarea del cronograma"
          deshabilitado={f.guardando}
        />
        {f.form.cambios.length > 0 && (
          <ul aria-label="Tareas que cambian" className="flex flex-col gap-3">
            {f.form.cambios.map((c) => {
              const nombre = nombreEtapa(admin, c.etapa_id);
              return (
                <li key={c.etapa_id}>
                  <fieldset aria-label={nombre} className="flex flex-col gap-2 rounded-control border border-borde p-3">
                    <Row justificar="entre">
                      <span className="font-medium">{nombre}</span>
                      <Button type="button" variante="fantasma" tamanio="sm" aria-label={`Quitar ${nombre}`} onClick={() => f.quitarCambio(c.etapa_id)}>
                        Quitar
                      </Button>
                    </Row>
                    <span className="text-chico text-texto-suave">{`Hoy: ${ETIQUETA_ESTADO_TAREA[estadoActual(admin, c.etapa_id, novedad)]}`}</span>
                    <Select
                      etiqueta="Estado nuevo"
                      opciones={OPCIONES_ESTADO}
                      valor={c.estado}
                      onCambio={(v) => f.cambiarEstado(c.etapa_id, v as EstadoTarea)}
                      error={f.errores.estados[c.etapa_id]}
                    />
                  </fieldset>
                </li>
              );
            })}
          </ul>
        )}

        <FotosNovedad fid={fid} f={f} />

        {novedad === null && (
          <Casilla etiqueta="Avisar por mail" ayuda="Les llega a fiduciantes, contador y escribano que tengan los avisos prendidos." marcado={f.form.avisar} onCambio={(v) => f.cambiar("avisar", v)} />
        )}

        <Alerta texto={f.errorFotos ?? f.errorGuardar} />
        <Row justificar="fin" espacio={2}>
          <Button type="button" variante="secundario" onClick={onCancelar}>
            Cancelar
          </Button>
          <Button type="submit" cargando={f.guardando}>
            Guardar
          </Button>
        </Row>
      </Stack>
    </form>
  );
}
